import React, { useState } from 'react';
import { MOODS, MoodId, getMood } from './moods';
import AnimatedMoodIllustration from './visual/AnimatedMoodIllustration';
import './MoodCheckIn.css';

/**
 * MoodCheckIn — the "How are you feeling today?" selector.
 *
 * Just the content (heading, the six mood cards, Skip / Continue). The caller
 * decides the frame: the session gate wraps it in a full-screen overlay; the
 * dashboard renders it inline inside a panel. Choosing a mood is never forced —
 * Skip is always offered and nothing is recorded here (the context decides what,
 * if anything, to persist).
 */
export interface MoodCheckInProps {
  onContinue: (mood: MoodId) => void;
  onSkip?: () => void;
  /** Fired the moment a mood is tapped (before Continue) — lets the frame react/tint. */
  onSelect?: (mood: MoodId) => void;
  initialMood?: MoodId | null;
  title?: string;
  subtitle?: string;
  /** Label for the primary button (e.g. "Continue" vs "Update mood"). */
  continueLabel?: string;
  /** While a save is in flight: disables actions and shows a saving label. */
  busy?: boolean;
}

export const MoodCheckIn: React.FC<MoodCheckInProps> = ({
  onContinue,
  onSkip,
  onSelect,
  initialMood = null,
  title = "How's your vibe today?",
  subtitle = 'Pick the feeling that matches your moment — no right answer, and you can always skip.',
  continueLabel = 'Continue',
  busy = false,
}) => {
  const [selected, setSelected] = useState<MoodId | null>(initialMood);
  const selectedMood = getMood(selected);

  const pick = (mood: MoodId) => {
    setSelected(mood);
    onSelect?.(mood);
  };

  return (
    <div
      className="mm-checkin"
      style={
        selectedMood
          ? { ['--mood-accent' as string]: selectedMood.accent, ['--mood-soft' as string]: selectedMood.soft }
          : undefined
      }
    >
      <div className="mm-checkin__head">
        <h1 className="mm-checkin__title">{title}</h1>
        <p className="mm-checkin__subtitle">{subtitle}</p>
      </div>

      <div className="mm-checkin__grid" role="radiogroup" aria-label="Select your mood">
        {MOODS.map((mood, i) => {
          const isActive = selected === mood.id;
          return (
            <button
              key={mood.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              className={`mm-mood-card ${isActive ? 'is-selected' : ''}`}
              style={{
                ['--mood-accent' as string]: mood.accent,
                ['--mood-soft' as string]: mood.soft,
                ['--mm-i' as string]: i,
              }}
              onClick={() => pick(mood.id)}
            >
              <span className="mm-mood-card__art" aria-hidden="true">
                <AnimatedMoodIllustration mood={mood.id} size="sm" accent={mood.accent} />
              </span>
              <span className="mm-mood-card__label">{mood.label}</span>
              <span className="mm-mood-card__tagline">{mood.tagline}</span>
              <span className="mm-mood-card__check" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mm-checkin__actions">
        {onSkip && (
          <button type="button" className="mm-btn mm-btn--ghost" onClick={onSkip} disabled={busy}>
            Skip for now
          </button>
        )}
        <button
          type="button"
          className="mm-btn mm-btn--primary"
          disabled={!selected || busy}
          onClick={() => selected && onContinue(selected)}
        >
          {busy ? 'Saving…' : continueLabel}
        </button>
      </div>

      <p className="mm-checkin__note">
        This is a personal check-in, not a diagnosis. You can change or skip it anytime.
      </p>
    </div>
  );
};

export default MoodCheckIn;
